/**
 * MechaGuard - BalanceConfig（平衡配置中心）
 *
 * 作用：把所有「可被运营调整」的数值集中成一个覆盖层（override），
 * 游戏运行时读取「默认值 + 覆盖层」的合并结果。覆盖层持久化到
 * localStorage，因此运营在 ?ops=1 下的调参会在刷新后依然生效。
 *
 * 设计要点
 *   - 本模块不依赖任何游戏实体（只依赖 Storage），避免循环引用。
 *   - 默认值由 main.init 通过 init() 注入（塔/敌人/波次的原始数据）。
 *   - 消费者在读取时负责合并默认值（见 Tower / getEnemyConfig / buildWaves），
 *     这样未初始化（Node 测试）时也能回退到各自的默认配置，不会崩。
 *   - 任何 update/reset/import 都会触发 onChange，让主程序把改动实时套用到
 *     当前对局（塔立即生效，敌人/波次对下一波或新刷怪生效）。
 */

import { readJson, writeJson } from '../systems/Storage.js';

const STORAGE_KEY = 'mechguard.balance.v1';

/** 全局战斗数值默认值（技能相关；运营面板可覆盖） */
export const GLOBAL_DEFAULTS = {
  orbitalDamage: 1000,     // 轨道打击伤害
  orbitalRadius: 92,       // 轨道打击作用半径
  shieldAmount: 5,         // 应急护盾每次提供的护盾点
  empFreezeDuration: 1.5,  // EMP 冻结全场敌人的持续时长（秒）
};

export class BalanceConfig {
  constructor() {
    this.defaults = { towers: {}, enemies: {}, waves: {}, global: {} };
    this.overrides = { towers: {}, enemies: {}, waves: {}, global: {} };
    this.storage = null;
    this._onChange = null;
  }

  /**
   * 用游戏默认数值初始化（main.init 里调用一次）。
   * @param {{towers?:object,enemies?:object,waves?:object,global?:object,storage?:object}} cfg
   */
  init({ towers = {}, enemies = {}, waves = {}, global = {}, storage = null } = {}) {
    this.defaults = { towers, enemies, waves, global };
    this.storage = storage;

    const loaded = readJson(storage, STORAGE_KEY, null);
    if (loaded && typeof loaded === 'object') {
      this.overrides = {
        towers: loaded.towers && typeof loaded.towers === 'object' ? loaded.towers : {},
        enemies: loaded.enemies && typeof loaded.enemies === 'object' ? loaded.enemies : {},
        waves: loaded.waves && typeof loaded.waves === 'object' ? loaded.waves : {},
        global: loaded.global && typeof loaded.global === 'object' ? loaded.global : {},
      };
    }
  }

  set onChange(fn) { this._onChange = fn; }

  /* ===================================================
   * 读取（默认值 + 覆盖层合并）
   * =================================================== */

  /** 塔数值：fallback 一般为 META.stats（未初始化/无覆盖时回退到默认） */
  getTowerStats(id, fallback = {}) {
    return Object.assign({}, fallback, this.overrides.towers[id] || {});
  }

  /** 塔售价：覆盖层 → 默认值 → fallback（META.cost） */
  getTowerCost(id, fallback = 0) {
    const over = this.overrides.towers[id];
    if (over && Number.isFinite(over.cost)) return over.cost;
    const def = this.defaults.towers[id];
    if (def && Number.isFinite(def.cost)) return def.cost;
    return fallback;
  }

  /** 敌人配置：按索引合并阶段（phases），保留默认形态名/颜色等 */
  getEnemyConfigMerged(id, base) {
    const over = this.overrides.enemies[id] || {};
    const merged = Object.assign({}, base, over);
    if (Array.isArray(base.phases)) {
      merged.phases = base.phases.map((p, i) =>
        Object.assign({}, p, (over.phases && over.phases[i]) || {}));
    }
    return merged;
  }

  /** 波次全局参数 */
  getWaveOptions() {
    return Object.assign({}, this.defaults.waves, this.overrides.waves);
  }

  /** 全局战斗数值（技能相关）：默认值 + 覆盖层合并 */
  getGlobal() {
    return Object.assign({}, this.defaults.global, this.overrides.global);
  }

  /* ===================================================
   * 写入（内存优先，persist 落盘，onChange 触发实时套用）
   * =================================================== */

  /**
   * 更新一项（不落盘；落盘请显式调 persist）。
   * @param {'towers'|'enemies'|'waves'|'global'} category
   * @param {string|null} id
   * @param {string} key
   * @param {number} value
   * @param {number} [phaseIndex] >=0 表示敌人阶段字段
   */
  update(category, id, key, value, phaseIndex = -1) {
    if (category === 'waves' || category === 'global') {
      this.overrides[category][key] = value;
    } else {
      const bucket = this.overrides[category];
      if (!bucket[id]) bucket[id] = {};
      if (phaseIndex >= 0) {
        if (!Array.isArray(bucket[id].phases)) bucket[id].phases = [];
        if (!bucket[id].phases[phaseIndex]) bucket[id].phases[phaseIndex] = {};
        bucket[id].phases[phaseIndex][key] = value;
      } else {
        bucket[id][key] = value;
      }
    }
    if (this._onChange) this._onChange();
  }

  /** 写入 localStorage */
  persist() {
    writeJson(this.storage, STORAGE_KEY, this.overrides);
  }

  /** 清空某类（id 省略则清空整类）并落盘 */
  resetCategory(category, id) {
    if (id) {
      if (this.overrides[category]) delete this.overrides[category][id];
    } else {
      this.overrides[category] = {};
    }
    this.persist();
    if (this._onChange) this._onChange();
  }

  /** 恢复全部默认（清空覆盖层） */
  resetAll() {
    this.overrides = { towers: {}, enemies: {}, waves: {}, global: {} };
    this.persist();
    if (this._onChange) this._onChange();
  }

  /** 导出当前覆盖层为 JSON 字符串（用于跨环境分享预设） */
  exportJSON() {
    return JSON.stringify(this.overrides, null, 2);
  }

  /** 从 JSON 字符串导入覆盖层（校验为对象即可，细字段交给各消费者合并） */
  importJSON(text) {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object') throw new Error('格式应为 JSON 对象');
    this.overrides = {
      towers: parsed.towers && typeof parsed.towers === 'object' ? parsed.towers : {},
      enemies: parsed.enemies && typeof parsed.enemies === 'object' ? parsed.enemies : {},
      waves: parsed.waves && typeof parsed.waves === 'object' ? parsed.waves : {},
      global: parsed.global && typeof parsed.global === 'object' ? parsed.global : {},
    };
    this.persist();
    if (this._onChange) this._onChange();
  }
}

/** 全局单例 */
export const balanceConfig = new BalanceConfig();
